<script setup lang="ts">

import { computed, ref } from 'vue'
import { useNow } from '@/composables/game/time'
import CountdownDisplay from '@/components/CountdownDisplay.vue'
import { usePlayer } from '@/composables/game/player'
import { useTonConnectModal } from '@townsquarelabs/ui-vue'
import { useWalletConnect } from '@/composables/game/wallet-connect'
import router from '@/router'
import { RouteName } from '@/routes'
import { useAchievements, useAchievementsPage } from '@/composables/game/achievements'
import { AchievementType } from '@/types/achievements'
import { useCurrentLevelRankFetcher } from '@/composables/game/leader-board'
import { DateTime } from 'luxon'
import SnapshotInfoModal from '@/components/modals/SnapshotInfoModal.vue'
import { env } from '@/env'
import season2Banner from '@/assets/images/periodic/tge/season2-banner.webp'
import BaseButton from '@/components/base/BaseButton.vue'
import LiveChip from '@/components/base/LiveChip.vue'
import WalletConnect from '@/components/WalletConnect.vue'

const { now } = useNow()
const { player } = usePlayer()
const { open } = useTonConnectModal()
useWalletConnect()
const { achievements, isFetchingAchievements, categoryGroups, categoryTitleMap } = useAchievementsPage(false)
const { getMilestone } = useAchievements()
const { currentRank, effortLeaderboardCurrentRank } = useCurrentLevelRankFetcher()

const isDev = computed(() => !env.app.isProd)

const snapShotTimestamp = DateTime.fromISO('2025-05-31T09:00:00', { zone: 'utc' }).toSeconds()
const formattedPostSnapshotText = DateTime
  .fromSeconds(snapShotTimestamp, { zone: 'utc' })
  .toFormat('\'Snapshot taken on\' dd LLL \'at\' h.mm a \'UTC\'')

const countdown = computed(() => {
  const timeDiff = snapShotTimestamp - now.value
  return Math.max(0, timeDiff)
})

const minLevel = 10

const playerWalletAddress = computed(() => player.value.settings.tonWalletAddress)
const userReachedMinLevel = computed(() => player.value.level >= minLevel)

const showSnapshotInfoModal = ref(false)

const faq = [
  {
    question: '1. What is the Dropee airdrop?',
    answer: `
      Our airdrop rewards players for their dedication to Dropee—recognizing activity, engagement, and loyalty with $DROPEE tokens when we launch.
    `,
  },
  {
    question: '2. Which metrics will we track?',
    answer: `
      We’ll consider three pillars of your in-game activity:
      <ul class="list-disc ml-5">
        <li class="mt-1"><span class="font-semibold text-white/[.87]">Dropee Journey:</span> Overall time and ranking in the game, loyalty streaks, and community referrals.</li>
        <li class="mt-1"><span class="font-semibold text-white/[.87]">Gameplay &amp; Activity:</span> Cards, quests, spins, combos, and general progression.</li>
        <li class="mt-1"><span class="font-semibold text-white/[.87]">Advanced &amp; Events:</span> Any advanced gameplay and special event achievements.</li>
      </ul>
    `,
  },
  {
    question: '3. How many phases are there?',
    answer: `
      <ul class="list-disc ml-5">
        <li class="mt-1"><span class="font-semibold text-white/[.87]">Phase 1: Snapshot (May 31 @ 09:00 UTC)</span><br>
            We capture your current metrics to establish a baseline score.
        </li>
        <li class="mt-1"><span class="font-semibold text-white/[.87]">Phase 2: Token Generation Event (Timing TBD)</span><br>
            When market conditions align, you’ll be able to claim your tokens—and anyone who keeps playing after May 31 may see their standing improve before the final launch.
        </li>
      </ul>
    `,
  },
  {
    question: '4. Can I keep improving after May 31?',
    answer: `
      Yes! While the snapshot locks in your baseline, continued play—leveling cards, completing quests, joining events—can give you an extra edge when allocations are finalized.
    `,
  },
  {
    question: '5. Do I need to link a wallet?',
    answer: `
      You will need a compatible wallet to claim your tokens once the TGE goes live. Details on wallet setup will be shared before Phase 2.
    `,
  },
  {
    question: '6. When can I actually claim my tokens?',
    answer: `
      We’ll announce the exact claim date as soon as market conditions are right. Keep an eye on official channels for updates.
    `,
  },
  {
    question: '7. What if I have questions or run into issues?',
    answer: `
      Drop us a message via <span class="font-semibold text-white/[.87]">@DropeeSupportBot</span> on Telegram. Our team will assist you within 48 hours.
    `,
  },
]

function onWalletConnectClick() {
  if (playerWalletAddress.value) return
  open()
}

function onReachLevelClick() {
  if (userReachedMinLevel.value) return
  router.push({ name: RouteName.MINE })
}

function getCurrentAchievementValue(categoryId: AchievementType) {
  if (!achievements.value || !(categoryId in achievements.value)) return 0
  return achievements.value[categoryId]
}
</script>
<template>
  <div class="flex-1 h-full w-full flex flex-col relative pb-5 mt-3">
    <div class="h-full w-full flex flex-col">
      <div class="w-full relative flex flex-col items-center justify-center overflow-visible text-center">
        <div class="relative my-4">
          <div class="token-glow absolute inset-0" />
          <img
            class="w-[85px] relative z-10"
            src="@/assets/images/periodic/tge/dropee-token.webp"
          >
        </div>
        <h1 class="font-pilat text-[20px] leading-7 font-semibold -mt-2.5">
          $DROPEE TOKEN
        </h1>
        <p class="text-white/60 font-semibold text-sm">
          Claim, Stake, and Power Up Your Empire
        </p>
      </div>
      <div class="relative my-6">
        <img :src="season2Banner">
        <LiveChip />
      </div>

      <div class="flex items-center gap-2 text-sm font-bold text-white/[.87] px-1 mb-4">
        <span class="h-px w-4 bg-white/[.38]" />
        <span class="whitespace-nowrap uppercase">ABOUT $DROPEE</span>
        <span class="h-px flex-1 bg-white/[.38]" />
      </div>

      <div
        v-if="!countdown || isDev"
        class="relative w-full mb-6 bg-dark-bottom-layer p-4 rounded-3xl"
      >
        <p class="text-sm text-white/[.87] font-semibold text-center mb-2">
          $DROPEE is the core token used across all Dropee games and apps.
        </p>
        <p class="text-sm text-white/[.60] font-semibold text-center">
          Own it, stake it, and use it to access exclusive benefits across the ecosystem.
        </p>
        <div class="w-full rounded-xl shadow-[0px_0px_38.6px_0px_rgba(230,185,12,0.45)] border-4 border-[#FFF246] bg-dark-top-most-layer py-2 px-0.5 mb-5 mt-10 text-center relative">
          <img
            class="absolute right-0 object-cover size-[72px] -top-10"
            src="@/assets/images/periodic/tge/airdrop-parachute.webp"
          >
          <span class="block font-pilat font-bold text-[22px] leading-8">TGE Date: May 27th</span>
          <span class="text-sm leading-6 font-semibold text-white/60 text-center">
            <a
              href="https://t.me/dropee_community"
              class="text-teal-primary underline"
              target="_blank"
            >Follow us</a> on our TG for live announcements
          </span>
        </div>
        <!-- <div class="flex items-center gap-2 text-sm font-bold text-white/60 px-1 mt-5 -ml-3">
          <span class="h-px w-4 bg-white/[.38]" />
          <span class="whitespace-nowrap uppercase">SEASON 1 ALLOCATION</span>
          <span class="h-px flex-1 bg-white/[.38]" />
        </div>
        <div class="flex flex-col p-4 pb-0 rounded-3xl bg-dark-bottom-layer">
          <div class="flex gap-3 font-semibold text-base mb-3">
            <span class="relative">
              <span class="h-6 flex items-center">
                <span class="rounded-full bg-[#999999] min-w-[14px] min-h-[14px] size-3.5 flex items-center justify-center">
                  <CheckCircleThin class="size-2.5 text-teal-primary" />
                </span>
              </span>
              <span class="absolute top-3.5 left-1/2 -translate-x-1/2 h-14 border-l border-dashed border-[#999999]" />
            </span>
            <p>
              <span class="block text-sm uppercase font-semibold text-white/[.87]">SEASON 1 - Phase 1</span>
              <span class="text-xs font-medium text-white/[.38]">ended 31 May 2025</span>
            </p>
            <p class="ml-auto flex gap-1 text-sm">
              <span class="text-white/[.87]">Snapshot</span>
              <span class="size-6 flex items-center justify-center">
                <CheckCircleThin class="size-6 text-teal-primary" />
              </span>
            </p>
          </div>
          <div class="flex gap-3 font-semibold text-base">
            <span class="relative">
              <span class="h-6 flex items-center">
                <span class="rounded-full bg-[#999999] min-w-[14px] min-h-[14px] size-3.5 flex items-center justify-center">
                  <CheckCircleThin class="size-2.5 text-teal-primary" />
                </span>
              </span>
            </span>
            <p>
              <span class="block text-sm uppercase font-semibold text-white/[.87]">Season 1 - Phase 2</span>
              <span class="text-xs font-medium text-white/[.38]">ended 19 Dec 2025</span>
            </p>
            <p class="ml-auto flex gap-1 text-sm">
              <span class="text-white/[.87]">Snapshot</span>
              <span class="size-6 flex items-center justify-center">
                <CheckCircleThin class="size-6 text-teal-primary" />
              </span>
            </p>
          </div>
          <div class="mt-3">
            <p class="flex justify-between text-lg font-bold pl-[26px]">
              <span class="block uppercase text-white/[.87]">TOTAL</span>
              <span class="text-teal-primary">Calculating...</span>
            </p>
          </div>
        </div>
        <div class="flex items-center gap-2 text-sm font-bold text-white/60 px-1 mt-4 -ml-3">
          <span class="h-px w-4 bg-white/[.38]" />
          <span class="whitespace-nowrap uppercase">SEASON 2 ALLOCATION</span>
          <span class="h-px flex-1 bg-white/[.38]" />
        </div>

        <div class="flex gap-3 px-4 pt-5 font-semibold text-base">
          <span class="relative">
            <span class="h-6 flex items-center">
              <span class="rounded-full bg-[#999999]/[.22] border-2 border-[#999999] min-w-[14px] min-h-[14px] size-3.5"></span>
            </span>
          </span>
          <p>
            <span class="block text-sm uppercase font-semibold text-white/[.87]">Season 2</span>
            <span class="text-xs font-medium text-white/[.38]">Keep playing & earn more!</span>
          </p>
          <p class="ml-auto flex gap-1">
            <span class="text-white/[.87]">Live</span>
            <span class="size-6 flex items-center justify-center">
              <span class="size-3.5 rounded-full min-w-[14px] bg-teal-primary pulse-circle" />
            </span>
            <p>
              <span class="block text-sm uppercase font-semibold text-white/[.87]">Season 2</span>
              <span class="text-xs font-medium text-white/[.38]">ended 25 May 2026</span>
            </p>
            <p class="ml-auto flex gap-1 text-sm">
              <span class="text-white/[.87]">Snapshot</span>
              <span class="size-6 flex items-center justify-center">
                <CheckCircleThin class="size-6 text-pink-primary" />
              </span>
            </p>
          </div>
          <div class="mt-3 mb-5">
            <p class="flex justify-between text-lg font-bold">
              <span class="block uppercase text-white/[.87]">TOTAL</span>
              <span class="text-pink-primary">Calculating...</span>
            </p>
          </div>
        </div> -->
        <BaseButton
          url="https://token.dropee.xyz"
          style-type="gold"
          class="w-full"
        >
          CLAIM HERE
        </BaseButton>
      </div>
      <template v-else>
        <div class="mt-8 text-center text-[20px] leading-7 font-semibold">
          Phase 1 snapshot in
        </div>
        <CountdownDisplay
          class="mt-3 w-full !gap-x-2"
          box-outer-class="shadow-[0px_0px_38.6px_0px_rgba(230,185,12,0.45)] bg-gold-gradient"
          :countdown="countdown"
        />
      </template>
      <div class="flex items-center gap-2 text-sm font-bold text-white/[.87] px-1 mb-3">
        <span class="h-px w-4 bg-white/[.38]" />
        <span class="whitespace-nowrap uppercase">Your wallet</span>
        <span class="h-px flex-1 bg-white/[.38]" />
      </div>
      <div class="bg-dark-bottom-layer rounded-3xl p-4 w-full mb-6">
        <WalletConnect
          :button-has-icon="false"
          :disable-disconnect="true"
        />
        <span class="text-xs font-medium text-white/60">This wallet will be used for the airdrop claim.</span>
      </div>

      <!-- $DROPEE UTILITY Section -->
      <div class="flex items-center gap-2 text-sm font-bold text-white/[.87] px-1 mb-4">
        <span class="h-px w-4 bg-white/[.38]" />
        <span class="whitespace-nowrap uppercase">$DROPEE UTILITY</span>
        <span class="h-px flex-1 bg-white/[.38]" />
      </div>
      <div class="bg-dark-bottom-layer rounded-3xl p-4 w-full mb-4">
        <div class="grid grid-cols-2 gap-1 mb-1">
          <div class="bg-dark-top-layer rounded-xl pt-2.5 pb-3 pl-4 pr-2">
            <span class="text-base">🈹</span>
            <p class="text-white font-semibold text-base">
              Special Discount
            </p>
            <p class="text-white/60 text-sm font-semibold">
              Save up to 20% when paying with $DROPEE
            </p>
          </div>
          <div class="bg-dark-top-layer rounded-xl pt-2.5 pb-3 pl-4 pr-2">
            <span class="text-base">🃏</span>
            <p class="text-white font-semibold text-base">
              Premium packs
            </p>
            <p class="text-white/60 text-sm font-semibold">
              Access limited offers at a discount
            </p>
          </div>
          <div class="bg-dark-top-layer rounded-xl pt-2.5 pb-3 pl-4 pr-2">
            <span class="text-base">🫅</span>
            <p class="text-white font-semibold text-base">
              Enter VIP events
            </p>
            <p class="text-white/60 text-sm font-semibold">
              Compete in elite leaderboards with high-tier prizes
            </p>
          </div>
          <div class="bg-dark-top-layer rounded-xl pt-2.5 pb-3 pl-4 pr-2">
            <span class="text-base">🏦</span>
            <p class="text-white font-semibold text-base">
              Stake & Earn
            </p>
            <p class="text-white/60 text-sm font-semibold">
              Lock tokens to receive ecosystem benefits
            </p>
          </div>
        </div>
        <div class="bg-dark-top-layer rounded-xl p-4 text-center">
          <span class="text-base">🏛</span>
          <p class="text-white font-semibold text-base">
            Trade freely
          </p>
          <p class="text-white/60 text-sm font-semibold">
            Soon on CEXs and DEXs
          </p>
        </div>
      </div>
      <p class="text-white/60 text-xs font-semibold text-center uppercase mb-6">
        More info coming soon
      </p>

      <!-- HOW TO EARN MORE $DROPEE Section -->
      <div class="flex items-center gap-2 text-sm font-bold text-white/[.87] px-1 mb-4">
        <span class="h-px w-4 bg-white/[.38]" />
        <span class="whitespace-nowrap uppercase">HOW TO EARN MORE $DROPEE</span>
        <span class="h-px flex-1 bg-white/[.38]" />
      </div>
      <div class="bg-dark-bottom-layer rounded-3xl p-4 w-full mb-6">
        <div class="bg-dark-top-layer rounded-xl pt-2.5 pb-3 pl-4 pr-2 flex items-center gap-4 mb-1">
          <span class="text-base">🎲</span>
          <p class="text-white font-semibold text-base">
            Play and Level up within Dropee games to earn more tokens
          </p>
        </div>
        <div class="bg-dark-top-layer rounded-xl pt-2.5 pb-3 pl-4 pr-2 flex items-center gap-4">
          <span class="text-base">💸</span>
          <p class="text-white font-semibold text-base">
            More ways to earn $DROPEE coming soon!
          </p>
        </div>
      </div>
    </div>

    <SnapshotInfoModal
      :show="showSnapshotInfoModal"
      @hide="showSnapshotInfoModal = false"
    />
  </div>
</template>
<style scoped lang="scss">
.gold-text {
	background: linear-gradient(105deg, #FFCB00 10.46%, #B57800 61.24%, #B77C07 70.47%, #BF881B 82.01%, #CB9D3C 95.86%, #DCB96A 112.01%, #F1DDA5 130.48%, #FFF4C9 139.71%, #EACE88 153.55%, #D8AD4E 167.4%, #CB9423 178.94%, #C38509 188.17%, #C08000 192.79%, #C58500 197.41%, #EAB200 227.41%, #F9C300 241.26%);
	background-clip: text;
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
}

.bg-gold-gradient {
	background-image: linear-gradient(
			to right,
			#fff246 0%,
			#e5b80b 37%,
			#fff571 69%,
			#e5b80b 100%
	);
}

@keyframes pulse-circle {
	0% {
		filter: drop-shadow(0px 0px 0px rgba(20, 241, 255, 0)); /* No shadow */
	}
	50% {
		filter: drop-shadow(0px 0px 4px #14F1FF);
	}
	100% {
		filter: drop-shadow(0px 0px 0px rgba(20, 241, 255, 0));
	}
}

.pulse-circle {
	animation: pulse-circle 1.5s infinite linear;
	will-change: filter;
}

.token-glow {
	width: 200%;
	height: 200%;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	background: radial-gradient(
		circle,
		rgba(180, 100, 200, 0.5) 0%,
		rgba(140, 60, 160, 0.3) 30%,
		rgba(100, 40, 120, 0.15) 50%,
		transparent 70%
	);
	filter: blur(20px);
	pointer-events: none;
}
</style>
